/**
 * App — root router.
 *
 * Architecture:
 * - "/" shows LandingPage for visitors, AppShell → HomePage for authenticated users
 * - "/sign-in" and "/status" are public routes without shell
 * - All other routes are wrapped in AppShell (sidebar + topbar) and RequireAuth
 *
 * Bundle strategy: only the first-paint surfaces (Splash, LandingPage,
 * SignInPage, AppShell, HomePage) are eagerly imported. Every other
 * route is ``React.lazy`` so Vite emits it as a separate chunk that
 * isn't fetched until the user navigates there. This is what keeps the
 * initial JS payload small enough that the dashboard renders quickly
 * even on a cold cache.
 */
import * as React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { AppShell } from "@/components/layout/AppShell";
import { Splash } from "@/components/landing/Splash";

// Eager: needed for the very first render path (visitors land on "/" =
// LandingPage; signed-in users land on "/home" = HomePage inside
// AppShell). Loading these via React.lazy adds a Suspense fallback to
// the first paint, which we don't want.
import { LandingPage } from "@/app/routes/LandingPage";
import { PrivacyPage } from "@/app/routes/PrivacyPage";
import { TermsPage } from "@/app/routes/TermsPage";
import { SignInPage } from "@/app/routes/SignInPage";
import { FirstAdminPage } from "@/app/routes/FirstAdminPage";
import { HomePage } from "@/app/routes/HomePage";

// Lazy: every other route. The import() call is wrapped in a small
// helper that handles the named-export → default-export shim React.lazy
// requires.
const lazyNamed = <K extends string>(
  loader: () => Promise<Record<K, React.ComponentType<unknown>>>,
  name: K,
) => React.lazy(async () => ({ default: (await loader())[name] }));

const StatusPage = lazyNamed(() => import("@/app/routes/StatusPage"), "StatusPage");
// Desktop Spotlight overlay surface (Electron loads this in a frameless window).
const OverlayPage = lazyNamed(() => import("@/app/routes/OverlayPage"), "OverlayPage");

const InboxPage = lazyNamed(() => import("@/app/routes/InboxPage"), "InboxPage");
const RunsPage = lazyNamed(() => import("@/app/routes/RunsPage"), "RunsPage");
const RunDetailPage = lazyNamed(() => import("@/app/routes/RunDetailPage"), "RunDetailPage");

const AgentsPage = lazyNamed(() => import("@/app/routes/AgentsPage"), "AgentsPage");
const OrchestrationPage = lazyNamed(() => import("@/app/routes/OrchestrationPage"), "OrchestrationPage");
const AgentDetailPage = lazyNamed(() => import("@/app/routes/AgentDetailPage"), "AgentDetailPage");
const TasksPage = lazyNamed(() => import("@/app/routes/TasksPage"), "TasksPage");
const AutomationsPage = lazyNamed(() => import("@/app/routes/AutomationsPage"), "AutomationsPage");
const RIDEPage = lazyNamed(() => import("@/app/routes/RIDEPage"), "RIDEPage");
const SkillsPage = lazyNamed(() => import("@/app/routes/SkillsPage"), "SkillsPage");
const MemoryPage = lazyNamed(() => import("@/app/routes/MemoryPage"), "MemoryPage");
const ProjectsPage = lazyNamed(() => import("@/app/routes/ProjectsPage"), "ProjectsPage");
const ProjectDetailPage = lazyNamed(() => import("@/app/routes/ProjectsPage"), "ProjectDetailPage");
const ArtifactsPage = lazyNamed(() => import("@/app/routes/ArtifactsPage"), "ArtifactsPage");

const DevicesPage = lazyNamed(() => import("@/app/routes/DevicesPage"), "DevicesPage");
const MCPHubPage = lazyNamed(() => import("@/app/routes/MCPHubPage"), "MCPHubPage");
const McpInstallPage = lazyNamed(() => import("@/app/routes/McpInstallPage"), "McpInstallPage");
const ToolsHubPage = lazyNamed(() => import("@/app/routes/ToolsHubPage"), "ToolsHubPage");
const ToolDetailPage = lazyNamed(() => import("@/app/routes/ToolDetailPage"), "ToolDetailPage");

const FilesPage = lazyNamed(() => import("@/app/routes/FilesPage"), "FilesPage");
const PermissionsPage = lazyNamed(() => import("@/app/routes/PermissionsPage"), "PermissionsPage");
const ObservabilityPage = lazyNamed(() => import("@/app/routes/ObservabilityPage"), "ObservabilityPage");
const SettingsHubPage = lazyNamed(() => import("@/app/routes/SettingsHubPage"), "SettingsHubPage");
const PreferencesPage = lazyNamed(() => import("@/app/routes/PreferencesPage"), "PreferencesPage");

const UsagePage = lazyNamed(() => import("@/app/routes/UsagePage"), "UsagePage");
const BillingPage = lazyNamed(() => import("@/app/routes/BillingPage"), "BillingPage");
const SetupWizardPage = lazyNamed(() => import("@/app/routes/SetupWizardPage"), "SetupWizardPage");

const WorkflowsPage = lazyNamed(() => import("@/app/routes/WorkflowsPage"), "WorkflowsPage");
const WorkflowEditorPage = lazyNamed(() => import("@/app/routes/WorkflowEditorPage"), "WorkflowEditorPage");
const WorkflowRunsPage = lazyNamed(() => import("@/app/routes/WorkflowRunsPage"), "WorkflowRunsPage");
const HealthPage = lazyNamed(() => import("@/app/routes/admin/HealthPage"), "HealthPage");
const CostsPage = lazyNamed(() => import("@/app/routes/admin/CostsPage"), "CostsPage");
const AuditPage = lazyNamed(() => import("@/app/routes/admin/AuditPage"), "AuditPage");
const SelfHostPage = lazyNamed(() => import("@/app/routes/admin/SelfHostPage"), "SelfHostPage");
const SettingsPage = lazyNamed(() => import("@/app/routes/admin/SettingsPage"), "SettingsPage");
const StorageAdminPage = lazyNamed(() => import("@/app/routes/admin/StorageAdminPage"), "StorageAdminPage");
const ControlsPage = lazyNamed(() => import("@/app/routes/admin/ControlsPage"), "ControlsPage");
const TiersPage = lazyNamed(() => import("@/app/routes/admin/TiersPage"), "TiersPage");
const UsersPage = lazyNamed(() => import("@/app/routes/admin/UsersPage"), "UsersPage");
const StorageDashboardPage = lazyNamed(
  () => import("@/app/routes/StorageDashboardPage"),
  "StorageDashboardPage",
);

/** Redirect to sign-in if not authenticated */
function RequireAuth() {
  const auth = useAuth();
  if (!auth.ready) return null;
  if (!auth.user) return <Navigate to="/sign-in" replace />;
  return <Outlet />;
}

/** Show landing page to visitors, redirect to /home for signed-in users */
function RootRoute() {
  const auth = useAuth();
  if (!auth.ready) return null;
  if (auth.user) return <Navigate to="/home" replace />;
  return <LandingPage />;
}

// Suspense fallback for lazy routes. Rendered inside AppShell so the
// chrome (sidebar, topbar) stays put while a route chunk loads.
function RouteFallback() {
  return <div className="p-6 text-fg-muted text-sm font-mono">Loading…</div>;
}

export function App() {
  const [splashDone, setSplashDone] = React.useState(() => {
    return sessionStorage.getItem("omni:splash-shown") === "1";
  });

  const handleSplashComplete = React.useCallback(() => {
    sessionStorage.setItem("omni:splash-shown", "1");
    setSplashDone(true);
  }, []);

  if (!splashDone) {
    return <Splash onComplete={handleSplashComplete} />;
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <React.Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public routes (no shell) */}
          <Route path="/" element={<RootRoute />} />
          <Route path="/sign-in" element={<SignInPage />} />
          {/* install.sh prints this link; public, like sign-in. */}
          <Route path="/admin/setup" element={<FirstAdminPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/overlay" element={<OverlayPage />} />

          {/* Authenticated routes wrapped in AppShell */}
          <Route element={<RequireAuth />}>
            <Route element={<AppShell />}>
              {/* Workspace */}
              <Route path="/home" element={<HomePage />} />
              <Route path="/inbox" element={<InboxPage />} />
              <Route path="/runs" element={<RunsPage />} />
              <Route path="/runs/:runId" element={<RunDetailPage />} />

              {/* Build */}
              <Route path="/agents" element={<AgentsPage />} />
              <Route path="/agents/:agentId" element={<AgentDetailPage />} />
              <Route path="/orchestration" element={<OrchestrationPage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/tasks/:task_id" element={<TasksPage />} />
              <Route path="/automations" element={<AutomationsPage />} />
              <Route path="/ride" element={<RIDEPage />} />
              <Route path="/skills" element={<SkillsPage />} />
              <Route path="/memory" element={<MemoryPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
              <Route path="/artifacts" element={<ArtifactsPage />} />

              {/* Connect */}
              <Route path="/devices" element={<DevicesPage />} />
              <Route path="/mcp-hub" element={<MCPHubPage />} />
              <Route path="/mcp" element={<McpInstallPage />} />
              <Route path="/tools" element={<ToolsHubPage />} />
              <Route path="/tools/:toolId" element={<ToolDetailPage />} />

              {/* Operate */}
              <Route path="/files" element={<FilesPage />} />
              <Route path="/permissions" element={<PermissionsPage />} />
              <Route path="/observability" element={<ObservabilityPage />} />
              <Route path="/settings" element={<SettingsHubPage />} />
              <Route path="/preferences" element={<PreferencesPage />} />
              <Route path="/usage" element={<UsagePage />} />
              <Route path="/settings/billing" element={<BillingPage />} />
              <Route path="/storage" element={<StorageDashboardPage />} />

              {/* Workflows (Phase 13 — on ADK) */}
              <Route path="/workflows" element={<WorkflowsPage />} />
              <Route path="/workflows/:workflow_id/editor" element={<WorkflowEditorPage />} />
              <Route path="/workflows/:workflow_id/runs" element={<WorkflowRunsPage />} />

              {/* Admin */}
              <Route path="/admin/health" element={<HealthPage />} />
              <Route path="/admin/costs" element={<CostsPage />} />
              <Route path="/admin/audit" element={<AuditPage />} />

              <Route path="/admin/self-host" element={<SelfHostPage />} />
              <Route path="/admin/settings" element={<SettingsPage />} />
              <Route path="/admin/storage" element={<StorageAdminPage />} />
              <Route path="/admin/controls" element={<ControlsPage />} />
              <Route path="/admin/policies/tiers" element={<TiersPage />} />
              <Route path="/admin/policies/users" element={<UsersPage />} />

              {/* VPS Setup Wizard */}
              <Route path="/setup" element={<SetupWizardPage />} />
            </Route>
          </Route>
        </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
}
